import { useCallback, useEffect, useMemo, useState } from "react";

import { fetchMembers, updateMemberContributionStatus, updateMembersContributionStatus } from "@/features/admin/api";
import { useAdminSession } from "@/features/admin/useAdminSession";
import type { AdminMember } from "@/features/admin/types";
import PaginationControls from "./PaginationControls";

const COTISATIONS_PER_PAGE = 20;

const cotisationConfig: Record<AdminMember["cotisation"], { label: string; cls: string; icon: string }> = {
  payee: { label: "Payée", cls: "bg-emerald-100 text-emerald-700", icon: "ri-checkbox-circle-line" },
  en_attente: { label: "En attente", cls: "bg-amber-100 text-amber-700", icon: "ri-time-line" },
  impayee: { label: "Impayée", cls: "bg-red-100 text-red-600", icon: "ri-close-circle-line" }
};

const actions: Array<{ status: Extract<AdminMember["cotisation"], "payee" | "impayee">; label: string; cls: string; icon: string }> = [
  { status: "payee", label: "Valider", cls: "bg-emerald-500 text-white hover:bg-emerald-600", icon: "ri-check-line" },
  { status: "impayee", label: "Impayée", cls: "bg-red-50 text-red-600 hover:bg-red-100", icon: "ri-close-line" }
];

export default function CotisationsTab() {
  const { request } = useAdminSession();
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setFeedback(null);

    try {
      const nextMembers = await fetchMembers(request);
      setMembers(nextMembers.filter((member) => member.statut === "actif"));
    } catch (caughtError) {
      setFeedback({
        type: "error",
        message: caughtError instanceof Error ? caughtError.message : "Impossible de charger les cotisations."
      });
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    setSelectedIds((current) => {
      const availableIds = new Set(members.map((member) => member.id));
      return new Set(Array.from(current).filter((id) => availableIds.has(id)));
    });
  }, [members]);

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return members;
    }

    return members.filter((member) =>
      `${member.prenom} ${member.nom}`.toLowerCase().includes(query)
      || (member.matricule ?? "").toLowerCase().includes(query)
      || (member.telephone ?? "").toLowerCase().includes(query)
      || (member.departement ?? "").toLowerCase().includes(query)
    );
  }, [members, search]);

  const stats = useMemo(() => ({
    total: members.length,
    payee: members.filter((member) => member.cotisation === "payee").length,
    en_attente: members.filter((member) => member.cotisation === "en_attente").length,
    impayee: members.filter((member) => member.cotisation !== "payee").length
  }), [members]);

  const totalPages = Math.max(1, Math.ceil(filteredMembers.length / COTISATIONS_PER_PAGE));
  const paginatedMembers = useMemo(() => {
    const startIndex = (currentPage - 1) * COTISATIONS_PER_PAGE;
    return filteredMembers.slice(startIndex, startIndex + COTISATIONS_PER_PAGE);
  }, [currentPage, filteredMembers]);
  const selectedCount = selectedIds.size;
  const isCurrentPageSelected = paginatedMembers.length > 0 && paginatedMembers.every((member) => selectedIds.has(member.id));

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const toggleMemberSelection = (memberId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);

      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }

      return next;
    });
  };

  const toggleCurrentPageSelection = () => {
    setSelectedIds((current) => {
      const next = new Set(current);

      if (isCurrentPageSelected) {
        paginatedMembers.forEach((member) => next.delete(member.id));
      } else {
        paginatedMembers.forEach((member) => next.add(member.id));
      }

      return next;
    });
  };

  const selectAllFilteredMembers = () => {
    setSelectedIds(new Set(filteredMembers.map((member) => member.id)));
  };

  const handleUpdateCotisation = async (member: AdminMember, status: AdminMember["cotisation"]) => {
    setUpdatingId(member.id);
    setFeedback(null);

    try {
      await updateMemberContributionStatus(request, member.id, status);
      setMembers((currentMembers) =>
        currentMembers.map((currentMember) =>
          currentMember.id === member.id ? { ...currentMember, cotisation: status } : currentMember
        )
      );
      setFeedback({
        type: "success",
        message: `Cotisation de ${member.prenom} ${member.nom} mise à jour.`
      });
    } catch (caughtError) {
      setFeedback({
        type: "error",
        message: caughtError instanceof Error ? caughtError.message : "Mise à jour de la cotisation impossible."
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleBulkValidate = async () => {
    const memberIds = Array.from(selectedIds);
    const selectedMemberIds = new Set(memberIds);

    if (memberIds.length === 0) {
      return;
    }

    setBulkUpdating(true);
    setFeedback(null);

    try {
      const result = await updateMembersContributionStatus(request, memberIds, "payee");
      setMembers((currentMembers) =>
        currentMembers.map((member) =>
          selectedMemberIds.has(member.id) ? { ...member, cotisation: "payee" } : member
        )
      );
      setSelectedIds(new Set());
      setFeedback({
        type: "success",
        message: `${result.updatedCount} cotisation(s) validée(s).`
      });
    } catch (caughtError) {
      setFeedback({
        type: "error",
        message: caughtError instanceof Error ? caughtError.message : "Validation groupée impossible."
      });
    } finally {
      setBulkUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Gérer cotisation</h1>
          <p className="mt-1 text-sm text-gray-500">Validez les cotisations des membres actifs.</p>
        </div>
        <button
          type="button"
          onClick={() => void loadMembers()}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#E8521A] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#d44a16]"
        >
          <i className="ri-refresh-line"></i>
          Actualiser
        </button>
      </div>

      {feedback ? (
        <div className={`rounded-xl px-4 py-3 text-sm font-medium ${feedback.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
          {feedback.message}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { label: "Membres actifs", value: stats.total, cls: "bg-gray-900 text-white" },
          { label: "Payées", value: stats.payee, cls: "bg-emerald-500 text-white" },
          { label: "En attente", value: stats.en_attente, cls: "bg-amber-400 text-gray-900" },
          { label: "Impayées", value: stats.impayee, cls: "bg-red-500 text-white" }
        ].map((item) => (
          <div key={item.label} className={`rounded-2xl p-4 ${item.cls}`}>
            <p className="text-xs font-semibold opacity-80">{item.label}</p>
            <p className="mt-2 text-2xl font-bold">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="relative">
        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
        <input
          type="text"
          placeholder="Rechercher par nom, matricule, téléphone ou direction..."
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setCurrentPage(1);
            setSelectedIds(new Set());
          }}
          className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-10 pr-4 text-sm transition-colors focus:border-[#E8521A] focus:outline-none"
        />
      </div>

      {!loading && filteredMembers.length > 0 ? (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={selectAllFilteredMembers}
            disabled={bulkUpdating || selectedCount === filteredMembers.length}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <i className="ri-checkbox-multiple-line"></i>
            Sélectionner tous les résultats
          </button>
        </div>
      ) : null}

      {selectedCount > 0 ? (
        <div className="flex flex-col gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-emerald-800">
            {selectedCount} membre(s) sélectionné(s)
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void handleBulkValidate()}
              disabled={bulkUpdating}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className={bulkUpdating ? "ri-loader-4-line animate-spin" : "ri-check-line"}></i>
              Valider la sélection
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkUpdating}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className="ri-close-line"></i>
              Effacer
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px]">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="w-12 px-5 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={isCurrentPageSelected}
                    onChange={toggleCurrentPageSelection}
                    disabled={paginatedMembers.length === 0 || bulkUpdating}
                    className="h-4 w-4 rounded border-gray-300 text-[#E8521A] focus:ring-[#E8521A]"
                    aria-label="Sélectionner les membres de cette page"
                  />
                </th>
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Membre</th>
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Direction</th>
                <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Cotisation</th>
                <th className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index}>
                    <td colSpan={5} className="px-5 py-4">
                      <div className="h-12 animate-pulse rounded-xl bg-gray-100"></div>
                    </td>
                  </tr>
                ))
              ) : paginatedMembers.map((member) => {
                const cotisation = cotisationConfig[member.cotisation];

                return (
                  <tr key={member.id} className="transition-colors hover:bg-gray-50">
                    <td className="px-5 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(member.id)}
                        onChange={() => toggleMemberSelection(member.id)}
                        disabled={bulkUpdating}
                        className="h-4 w-4 rounded border-gray-300 text-[#E8521A] focus:ring-[#E8521A]"
                        aria-label={`Sélectionner ${member.prenom} ${member.nom}`}
                      />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <img src={member.avatar ?? ""} alt={member.nom} className="h-10 w-10 flex-shrink-0 rounded-full object-cover" />
                        <div>
                          <p className="text-sm font-semibold text-[#1A1A1A]">{member.prenom} {member.nom}</p>
                          <p className="text-xs text-gray-500">{member.matricule ?? member.telephone ?? "Identifiant non renseigné"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-600">{member.departement ?? "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${cotisation.cls}`}>
                        <i className={cotisation.icon}></i>
                        {cotisation.label}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {actions.map((action) => (
                          <button
                            key={action.status}
                            type="button"
                            onClick={() => void handleUpdateCotisation(member, action.status)}
                            disabled={bulkUpdating || updatingId === member.id || member.cotisation === action.status}
                            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-45 ${action.cls}`}
                          >
                            <i className={updatingId === member.id ? "ri-loader-4-line animate-spin" : action.icon}></i>
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filteredMembers.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">
            <i className="ri-wallet-3-line mb-3 block text-4xl"></i>
            Aucun membre actif trouvé.
          </div>
        ) : null}
      </div>

      {!loading && filteredMembers.length > 0 ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemLabel="membres cotisations"
        />
      ) : null}
    </div>
  );
}
